import { Amplify } from 'aws-amplify';
import outputs from './amplify_outputs'; // Assuming file is in project root

import React, { useState, useEffect } from 'react';
import { StyleSheet, View, Button, Image, Alert, Platform } from 'react-native'; // Import Platform
import * as ImagePicker from 'expo-image-picker';
import * as MediaLibrary from 'expo-media-library';
import * as FileSystem from 'expo-file-system';
import { uploadImage, processImage } from './services';

// Configure Amplify
Amplify.configure(outputs);

export default function App() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [modifiedImage, setModifiedImage] = useState<string | null>(null);
  const [hasMediaLibraryPermission, setHasMediaLibraryPermission] = useState<boolean | null>(null); // Allow null initial state
  const [isUploading, setIsUploading] = useState<boolean>(false);

  // Request permission - adjusted for web compatibility (MediaLibrary might not be applicable/needed for saving on web)
  useEffect(() => {
    (async () => {
      if (Platform.OS !== 'web') {
        const { status } = await MediaLibrary.requestPermissionsAsync();
        setHasMediaLibraryPermission(status === 'granted');
      } else {
        setHasMediaLibraryPermission(true); // Assume permission granted or not needed for web saving via download link
      }
    })();
  }, []);

  // Launch the image picker.
  const pickImage = async (): Promise<void> => {
    let result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 1,
      // Request base64 data on native for easier handling, web will use URI
      base64: Platform.OS !== 'web', 
    });

    if (!result.canceled && result.assets && result.assets[0]) {
      const asset = result.assets[0];
      setSelectedImage(asset.uri); // Keep displaying the selected image URI
      handleUpload(asset); // Pass the whole asset
    }
  };

  // Handle image upload using the service - adapted for cross-platform data handling
  const handleUpload = async (asset: ImagePicker.ImagePickerAsset): Promise<void> => {
    setIsUploading(true);
    try {
      let imageData: Blob | { base64: string; mimeType?: string };
      let contentType: string | undefined = asset.mimeType;

      if (Platform.OS === 'web') {
        // Fetch the blob from the URI on web
        const response = await fetch(asset.uri);
        imageData = await response.blob();
        contentType = imageData.type; // Use blob's type
      } else {
        // Use base64 directly if available (requested in pickImage for native)
        if (!asset.base64) {
           throw new Error('Base64 data is missing for native platform.');
        }
         imageData = { base64: asset.base64, mimeType: asset.mimeType };
         contentType = asset.mimeType; // Use asset's mimeType
      }

       // Determine file extension
       const uriParts = asset.uri.split('.');
       const fileExtension = uriParts[uriParts.length - 1];

      const uploadResult = await uploadImage(imageData, contentType, fileExtension);

      if (uploadResult.success && uploadResult.url && uploadResult.bucketName) {
        console.log('Image uploaded:', uploadResult.url);

        // Extract the image key (path within the bucket)
        // The 'url' from uploadImage service now likely contains just the path key
        const imageKey = uploadResult.url; 

        // Call the MinecraftMe Lambda function
        const processResult = await processImage(uploadResult.bucketName, imageKey);
        if (processResult.success && processResult.imageUrl) {
          setModifiedImage(processResult.imageUrl);
          Alert.alert('Success', 'Image processed successfully!');
        } else {
          Alert.alert('Error', processResult.error || 'Failed to process image');
        }
      } else {
        console.error('Upload failed:', uploadResult.error);
        Alert.alert('Error', uploadResult.error || 'Upload failed');
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      Alert.alert('Error', `Failed to upload image: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  // Save the modified image - adapted for web download
  const saveImage = async (): Promise<void> => {
     if (!modifiedImage) return;

    if (Platform.OS === 'web') {
      try {
        // Create a temporary link to trigger download
        const link = document.createElement('a');
        link.href = modifiedImage;
        // Suggest a filename (optional)
        const filename = modifiedImage.split('/').pop() || 'modifiedImage.jpg';
        link.download = filename; 
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        Alert.alert('Success', 'Image download initiated.');
      } catch (error) {
         console.error(error);
         Alert.alert('Error', 'Failed to initiate image download.');
      }
    } else {
      // Native saving logic
      if (!hasMediaLibraryPermission) {
        Alert.alert('Permission required', 'Please grant media library permissions.');
        return;
      }
      try {
        const localUri = FileSystem.documentDirectory + (modifiedImage.split('/').pop() || 'modifiedImage.jpg');
        const { uri } = await FileSystem.downloadAsync(modifiedImage, localUri);
        await MediaLibrary.saveToLibraryAsync(uri);
        Alert.alert('Success', 'Image saved to your gallery.');
      } catch (error) {
        console.error(error);
        Alert.alert('Error', 'Failed to save image.');
      }
    }
  };

  return (
    <View style={styles.container}>
      <Button 
        title="Pick an image" 
        onPress={pickImage} 
        disabled={isUploading}
      />
      {selectedImage && (
        <Image source={{ uri: selectedImage }} style={styles.image} />
      )}
      {isUploading && <Alert>Uploading...</Alert>} {/* Added basic loading indicator */}
      {modifiedImage && (
        <>
          <Image source={{ uri: modifiedImage }} style={styles.image} />
          <Button 
            title={Platform.OS === 'web' ? "Download Modified Image" : "Save Modified Image"} 
            onPress={saveImage} 
            disabled={isUploading} // Assuming saving is quick, but disable if download/save takes time
          />
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  image: {
    width: 300,
    height: 300,
    marginVertical: 10,
    resizeMode: 'contain',
  },
});
